import appStore from '../utils/state';
import gsap from 'gsap';
import { ProductModalType } from '../types';

export default class ProductModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private dialog: HTMLDialogElement | null = null;
  private closeButtons: HTMLElement[] = [];

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromDetailsAndCareModalState: (() => void) | null = null;
  private unsubscribeFromSizeAndFitModalState: (() => void) | null = null;
  private unsubscribeFromSustainabilityModalState: (() => void) | null = null;
  private unsubscribeFromDeliveryAndReturnsModalState: (() => void) | null = null;
  private unsubscribeFromDescriptionModalState: (() => void) | null = null;
  private unsubscribeFromGiftcardInstructionsModalState: (() => void) | null = null;
  private unsubscribeFromGiftcardWarningModalState: (() => void) | null = null;
  private unsubscribeFromShopTheLookModalState: (() => void) | null = null;
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onToggleBound = this.onToggle.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);

  private type: ProductModalType | null = null;

  constructor() {
    super();
  }

  connectedCallback(): void {
    this.type = this.dataset.type as ProductModalType;
    this.dialog = this.querySelector('dialog');
    this.closeButtons = Array.from(this.querySelectorAll('[data-product-modal-ref="close-button"]'));

    this.addEventListeners();
    this.subscribe();
  }

  addEventListeners(): void {
    this.closeButtons.forEach((button) => {
      button.addEventListener('click', this.onCloseButtonClickBound);
    });
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }
  }

  removeEventListeners(): void {
    this.closeButtons.forEach((button) => {
      button.removeEventListener('click', this.onCloseButtonClickBound);
    });
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }
  }

  subscribe(): void {
    switch (this.type) {
      case 'details-and-care':
        this.unsubscribeFromDetailsAndCareModalState = appStore.subscribeKey(
          'isDetailsAndCareModalOpen',
          this.onToggleBound,
        );
        break;
      case 'size-and-fit':
        this.unsubscribeFromSizeAndFitModalState = appStore.subscribeKey('isSizeAndFitModalOpen', this.onToggleBound);
        break;
      case 'sustainability':
        this.unsubscribeFromSustainabilityModalState = appStore.subscribeKey(
          'isSustainabilityModalOpen',
          this.onToggleBound,
        );
        break;
      case 'delivery-and-returns':
        this.unsubscribeFromDeliveryAndReturnsModalState = appStore.subscribeKey(
          'isDeliveryAndReturnsModalOpen',
          this.onToggleBound,
        );
        break;
      case 'description':
        this.unsubscribeFromDescriptionModalState = appStore.subscribeKey('isDescriptionModalOpen', this.onToggleBound);
        break;
      case 'giftcard-instructions':
        this.unsubscribeFromGiftcardInstructionsModalState = appStore.subscribeKey(
          'isGiftcardInstructionsModalOpen',
          this.onToggleBound,
        );
        break;
      case 'giftcard-warning':
        this.unsubscribeFromGiftcardWarningModalState = appStore.subscribeKey(
          'isGiftcardWarningModalOpen',
          this.onToggleBound,
        );
        break;
      case 'shop-the-look':
        this.unsubscribeFromShopTheLookModalState = appStore.subscribeKey('isShopTheLookModalOpen', this.onToggleBound);
        break;
    }
  }

  unsubscribe(): void {
    if (this.unsubscribeFromDetailsAndCareModalState) {
      this.unsubscribeFromDetailsAndCareModalState();
    }
    if (this.unsubscribeFromSizeAndFitModalState) {
      this.unsubscribeFromSizeAndFitModalState();
    }
    if (this.unsubscribeFromSustainabilityModalState) {
      this.unsubscribeFromSustainabilityModalState();
    }
    if (this.unsubscribeFromDeliveryAndReturnsModalState) {
      this.unsubscribeFromDeliveryAndReturnsModalState();
    }
    if (this.unsubscribeFromDescriptionModalState) {
      this.unsubscribeFromDescriptionModalState();
    }
    if (this.unsubscribeFromGiftcardInstructionsModalState) {
      this.unsubscribeFromGiftcardInstructionsModalState();
    }
    if (this.unsubscribeFromGiftcardWarningModalState) {
      this.unsubscribeFromGiftcardWarningModalState();
    }
    if (this.unsubscribeFromShopTheLookModalState) {
      this.unsubscribeFromShopTheLookModalState();
    }
  }

  close(): void {
    switch (this.type) {
      case 'details-and-care':
        appStore.actions.closeDetailsAndCareModal();
        break;
      case 'size-and-fit':
        appStore.actions.closeSizeAndFitModal();
        break;
      case 'sustainability':
        appStore.actions.closeSustainabilityModal();
        break;
      case 'delivery-and-returns':
        appStore.actions.closeDeliveryAndReturnsModal();
        break;
      case 'description':
        appStore.actions.closeDescriptionModal();
        break;
      case 'giftcard-instructions':
        appStore.actions.closeGiftcardInstructionsModal();
        break;
      case 'giftcard-warning':
        appStore.actions.closeGiftcardWarningModal();
        break;
      case 'shop-the-look':
        appStore.actions.closeShopTheLookModal();
        break;
    }
  }

  isCurrentModalOpen(): boolean {
    const state = appStore.getState();

    switch (this.type) {
      case 'details-and-care':
        return state.isDetailsAndCareModalOpen;
      case 'size-and-fit':
        return state.isSizeAndFitModalOpen;
      case 'sustainability':
        return state.isSustainabilityModalOpen;
      case 'delivery-and-returns':
        return state.isDeliveryAndReturnsModalOpen;
      case 'description':
        return state.isDescriptionModalOpen;
      case 'giftcard-instructions':
        return state.isGiftcardInstructionsModalOpen;
      case 'giftcard-warning':
        return state.isGiftcardWarningModalOpen;
      case 'shop-the-look':
        return state.isShopTheLookModalOpen;
      default:
        return false;
    }
  }

  onCloseButtonClick(): void {
    this.close();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    this.close();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      this.close();
    }
  }

  onDialogClose(): void {
    if (this.isCurrentModalOpen()) {
      this.close();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = this.dialog?.id ? document.querySelector(`[aria-controls="${this.dialog.id}"]`) : null;

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onToggle(isOpen: boolean): void {
    if (isOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { x: '5%' }, { x: 0, force3D: true, duration: 0.7, ease: 'expo.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline({
      onComplete: () => {
        if (this.dialog?.open) {
          this.dialog.close();
        }
      },
    });

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    this.hideTl.to(this.dialog, { x: '5%', duration: 0.3, force3D: true, ease: 'expo.out' }, 0);
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();
  }
}

customElements.define('product-modal', ProductModal);
